<script>
  // Shared trades / blotter table (U7) — lifted verbatim from BacktestResults.svelte. Consumed by Backtester,
  // Paper, and Live, fed the neutral result's `trades` (StrategyTradeRecord[]). Behavior-preserving: the
  // per-trade realized-P&L column is OMITTED (the backtest path returns 0 for every fill, and the live path
  // has no per-fill P&L attribution at all — so there is no honest column to show).
  import { formatCurrency } from '../../lib/store.js';
  import { fromRequestSymbol } from '../Backtesting/SymbolMultiSelect.svelte';
  import { getCryptoIconOrLetters, getCryptoGradient } from '../../lib/cryptoIcons.js';

  export let trades = [];

  function sideClass(side) {
    return String(side).toLowerCase() === 'sell' ? 'side-sell' : 'side-buy';
  }

  // Symbol display (mirrors SymbolMultiSelect): trades arrive in delimiter form (BTC_USD / BTC-USD).
  function displaySymbol(sym) {
    return fromRequestSymbol(sym ?? '');
  }
  function symbolBase(sym) {
    return displaySymbol(sym).split('/')[0];
  }
  function symbolIcon(sym) {
    return getCryptoIconOrLetters(symbolBase(sym));
  }
  function symbolGradient(sym) {
    return getCryptoGradient(symbolBase(sym));
  }

  function fmtTime(t) {
    if (!t) return '—';
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  }

  // Total trade amount in quote currency = price × qty (the notional moved by the fill).
  function tradeAmount(t) {
    const price = Number(t.price);
    const qty = Number(t.qty);
    if (Number.isNaN(price) || Number.isNaN(qty)) return 0;
    return price * qty;
  }

  // Order lifecycle status (live/paper carry Placing/Done/PartialFilled/Rejected/Cancelled/Unknown; backtest
  // stamps "Done"). Empty/absent → em-dash. The class drives a status pill color (success/danger/neutral).
  function statusLabel(s) {
    return s ? String(s) : '—';
  }

  // TASK-20260825-twap-parent-rows-illegible-in-trades-tab (AC 6-8). A TWAP parent's ledger row is a
  // write-ahead intent for the WHOLE parent and is `Placing` BY DESIGN until the parent settles, so it read
  // as a hung order. When TradesTab has resolved the parent's summary it hands the row a `twap` block; we
  // then show the cycle progress instead of the bare lifecycle pill.
  //
  // Rows WITHOUT that block — every backtest fill, every portfolio-blotter row, an ordinary Placing order, and
  // a twp… row whose summary could not be resolved — fall through to exactly the behavior above (AC 9-11).
  // This table is shared by three consumers; only the decorated path may differ.
  //
  // The pill TONE is resolved in tradesTwapParents.js, not by statusClass here: the parent status vocabulary
  // (Working/Abandoned/...) is not the ledger vocabulary, and `Abandoned` — a recovery that could not resume —
  // has no arm in statusClass and fell through to unstyled grey, reading like a benign cancel.
  function twapOf(t) {
    return t && t.twap ? t.twap : null;
  }
  function statusClass(s) {
    const v = String(s ?? '').toLowerCase();
    if (v === 'done' || v === 'filled') return 'status-done';
    if (v === 'partialfilled') return 'status-partial';
    if (v === 'rejected') return 'status-rejected';
    if (v === 'cancelled' || v === 'canceled') return 'status-cancelled';
    if (v === 'placing' || v === 'unknown') return 'status-pending';
    return '';
  }
</script>

<div class="bt-trades">
  <h3 class="bt-trades-title">Trades</h3>
  {#if trades.length === 0}
    <div class="empty-state">
      <h4 class="empty-state-title">No trades generated</h4>
      <p class="empty-state-description">This strategy did not open any positions over the window.</p>
    </div>
  {:else}
    <!-- Per-trade P&L column OMITTED (backtest returns 0; live has no per-fill attribution — KTD7/R11). -->
    <div class="bt-trades-table" role="table" aria-label="Trades">
      <div class="bt-trades-head" role="row">
        <span role="columnheader">Symbol</span>
        <span role="columnheader">Side</span>
        <span role="columnheader">Status</span>
        <span role="columnheader">Time</span>
        <span role="columnheader" class="num">Price</span>
        <span role="columnheader" class="num">Qty</span>
        <span role="columnheader" class="num">Tot Trade Amt</span>
        <span role="columnheader" class="num">Fee</span>
      </div>
      <div class="bt-trades-body">
        {#each trades as t, i (i)}
          {@const icon = symbolIcon(t.symbol)}
          {@const twap = twapOf(t)}
          <div class="bt-trades-row" role="row">
            <span role="cell" class="bt-symbol-cell">
              <span
                class="bt-symbol-icon"
                class:icon-symbol={icon.type === 'icon'}
                style="background: {symbolGradient(t.symbol)}"
                aria-hidden="true"
              >{icon.value}</span>
              <span class="bt-symbol-label">{displaySymbol(t.symbol)}</span>
            </span>
            <span role="cell" class="bt-side {sideClass(t.side)}">{t.side}</span>
            <span role="cell">
              {#if twap}
                <span class="bt-status bt-status-twap {twap.tone}" title="TWAP parent — {statusLabel(twap.status)}"
                >{twap.label}</span>
              {:else}
                <span class="bt-status {statusClass(t.status)}">{statusLabel(t.status)}</span>
              {/if}
            </span>
            <span role="cell">{fmtTime(t.tradeTime)}</span>
            <span role="cell" class="num">{formatCurrency(t.price)}</span>
            <span role="cell" class="num">{t.qty}</span>
            <span role="cell" class="num">{formatCurrency(tradeAmount(t))}</span>
            <span role="cell" class="num">{formatCurrency(t.fee)}</span>
          </div>
        {/each}
      </div>
    </div>
  {/if}
</div>

<style>
  @import '../Backtesting/BacktestResults.css';

  /* 8 columns now (Status added between Side and Time). Override the shared 7-column grid so every consumer
     (Backtester, Paper, Live) lays the Status column out consistently. */
  .bt-trades :global(.bt-trades-head),
  .bt-trades :global(.bt-trades-row) {
    grid-template-columns: 1.1fr 0.7fr 0.9fr 1.4fr 1fr 1fr 1.1fr 0.8fr;
  }

  /* Status pill — a compact, color-coded lifecycle marker. */
  .bt-status {
    display: inline-block;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1.3;
    background: var(--surface-muted, #2a2b2e);
    color: var(--text-muted, #b6b7b9);
    white-space: nowrap;
  }
  .bt-status.status-done { background: rgba(22, 163, 74, 0.18); color: var(--color-success, #16a34a); }
  .bt-status.status-partial { background: rgba(202, 138, 4, 0.18); color: #ca8a04; }
  .bt-status.status-rejected { background: rgba(220, 38, 38, 0.18); color: var(--color-danger, #dc2626); }
  .bt-status.status-cancelled { background: rgba(120, 120, 130, 0.18); color: var(--text-muted, #8a8b8d); }
  .bt-status.status-pending { background: rgba(59, 130, 246, 0.18); color: #3b82f6; }

  /* A working TWAP parent: progressing by design, so it must read as active — not as the blue "pending"
     that an ordinary stuck placement wears, and not as a failure. A SETTLED parent keeps its terminal
     colour (status-done / status-cancelled / …) and only the label carries the cycle count. */
  .bt-status.bt-status-twap { letter-spacing: 0.01em; }
  .bt-status.status-twap-working { background: rgba(139, 92, 246, 0.18); color: #8b5cf6; }
</style>
